import axios from 'axios';
import toast from '@/notify';
import { i18n } from '@/i18n';
import { setBaseURL } from '@/helpers/domain';

const baseURL = setBaseURL();

export const http = axios.create({
  baseURL,
  withCredentials: true,
  headers: {
    'X-Authorization': JSON.parse(sessionStorage.getItem('candidateToken'))?.token || '',
  },
});

export const checkCandidateAgreement = (applicationId) => http.get(
  '/testing-candidate/stage',
  {
    params: {
      applicationId,
    },
  },
);

export const confirmAgreementRequest = (userId, consentData = true) => http.post(
  `/applications/${userId}/consents/confirmation `, {
    consentData,
  },
);

export const getOffices = (segment = null) => http.get(
  '/offices-candidate',
  {
    params: {
      code: null,
      query: null,
      regionIds: null,
      segment,
    },
  },
);

/**
 * @param {string} office
 * @returns {Promise<AxiosResponse<any>>}
 */
export const getOfficeById = (office) => http.get(
  '/offices-candidate/landing',
  { params: { office } },
);

export const getRegions = () => http.get('/regions', { params: { page: 0, size: 9999 } });
export const getCandidateRegions = (segment = null) => http.get('/regions-candidate', {
  params: {
    segment,
  },
});
export const getCountries = () => http.get('/countries');
export const getWorkSchedules = () => http.get('/work-schedules');
export const getVacancies = (segment = null) => http.get('/vacancies-candidate/positions', {
  params: {
    page: 0,
    size: 9999,
    segment,
  },
});
export const getVacancy = ({ position, office }) => http.get(
  '/vacancies-candidate/landing',
  {
    params: { position, office },
  },
);
export const getCandidateContractor = ({ officeId }) => http.get(
  '/offices-candidate/contractor',
  {
    params: { officeId },
  },
);
export const postCandidate = (payload) => http.post('/candidates', payload);
export const updateCandidateData = (id, payload, token) => http.put(
  `/candidates/${id}`,
  payload,
  {
    headers: {
      'X-Authorization-Candidate': token,
    },
  },
);
export const updateCandidateDetail = (id, payload, token) => http.put(
  `/applications/${id}`,
  payload,
  {
    headers: {
      'X-Authorization-Candidate': token,
    },
  },
);

export const getTelegramLink = (id) => http.get(
  `/applications/${id}/landing`,
);

export const postResume = (id, cv, token) => http.post(
  `/applications/${id}/cv`,
  cv,
  {
    headers: {
      'X-Authorization-Candidate': token,
      'Content-Type': 'multipart/form-data',
    },
  },
);

export const checkDuplicates = (id, token) => http.get(
  `/applications/${id}/duplicates`,
  {
    headers: {
      'X-Authorization-Candidate': token,
    },
  },
);

export const aplicationConfirm = (id, token) => http.post(
  `/applications/${id}/statuses/confirm`,
  null,
  {
    headers: {
      'X-Authorization-Candidate': token,
    },
  },
);

export const selfReject = (id, token) => http.post(
  `/applications/${id}/statuses/self-reject`,
  {
    reason: 'self-reject',
    step: 'LANDING',
  },
  {
    headers: {
      'X-Authorization-Candidate': token,
    },
  },
);

export const getTemplate = (applicationId) => http.get(
  '/videointerviews-candidate',
  {
    params: {
      applicationId,
    },
  },
);

export const getVideointerviewTenplate = (applicationId) => http.get(
  '/videointerviews-candidate/template',
  {
    params: {
      applicationId,
    },
  },
);

export const createInterview = (payload) => http.post('/videointerviews-candidate', payload);

export const getInterview = (applicationId) => http.get(`/videointerviews/${applicationId}`);

export const finishInterview = (id) => http.post(`/videointerviews-candidate/${id}/finish`);

export const sendCandidateAnswer = (file, questionId, videointerviewId) => http.post(
  `/videointerviews-candidate/${videointerviewId}/questions/${questionId}`,
  file,
  {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  },
);

export const updateCandidateAnswer = (file, questionId, videointerviewId) => http.put(
  `/videointerviews-candidate/${videointerviewId}/questions/${questionId}`,
  file,
);

/**
 * @param {string} id
 * @param {string} transitionId
 * @returns {Promise<AxiosResponse<any>>}
 */
export const changeApplicationStatus = (id, transitionId) => http.post(
  `/applications/${id}/statuses`,
  {
    transitionDetails: '',
    transitionId,
  },
);

export const getPublicApplicationById = (id, token) => http.get(
  `/applications/public/${id}/overview`,
  {
    headers: {
      'X-Authorization': token,
    },
  },
);

export const getSecurityData = (id) => http.get(`/security-profile-candidate?applicationId=${id}`);
export const postSecurityData = (payload) => http.post('/security-profile-candidate', payload);

export const getRejectReasons = () => http.get('/rejection-reasons/candidate');

export const postSelfReject = (id, reason) => http.post(`/applications/${id}/statuses/self-reject`, { reason });

export const getSurveyById = (id) => http.get(
  '/surveys-candidate',
  { params: { applicationId: id } },
);

export const getSurveyTemplateById = (id) => http.get(
  '/surveys-candidate/template',
  { params: { applicationId: id } },
);

export const createSurvey = (payload) => http.post(
  '/surveys-candidate',
  payload,
);

export const postAnswerSurvey = (surveyId, questionId, payload) => http.post(
  `/surveys-candidate/${surveyId}/questions/${questionId}`,
  payload,
);

export const getCurrentCandidateTestingStage = (applicationId) => http.get(
  'testing-candidate/stage',
  { params: { applicationId } },
);

const errorMessages = new Map([
  ['fullName', i18n.t('errors.full-name-must-contain-three-words')],
  ['phone', i18n.t('errors.phone-must-have-format')],
  ['Conflict', i18n.t('errors.video-interview-has-already-been-created')],
]);

export const postReference = (applicationId, reference, token) => http.post(
  '/security-profile-candidate/files',
  reference,
  {
    headers: {
      'X-Authorization-Candidate': token,
      'Content-Type': 'multipart/form-data',
    },
    params: {
      applicationId,
    },
  },
);

export const removeReference = (applicationId, filePath, token) => http.delete(
  '/security-profile-candidate/files',
  {
    params: {
      'X-Authorization-Candidate': token,
      applicationId,
      filePath,
    },
  },
);

export const initialize = () => {
  http.interceptors.response.use(
    (response) => response,
    (error) => {
      const { status, data } = error?.response;
      if (status === 500) {
        toast.danger({
          text: i18n.t('errors.service-is-unavailable'),
        });
      }

      if ([400, 409].includes(status)) {
        const errors = Object.keys(data.error);
        errors.forEach((k) => {
          if (errorMessages.has(k)) {
            toast.danger({
              text: errorMessages.get(k),
            });
          }
        });
      }
      return error.response;
    },
  );
};
